import React from 'react';
import { motion } from 'framer-motion';
import { Home, Key, Hammer, Users, Heart, Shield, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const GIVEBUTTER_URL = "https://givebutter.com/XT2qPn";

const HousingPage = () => {
  const navigate = useNavigate();

  const handleDonate = () => {
    window.open(GIVEBUTTER_URL, '_blank', 'noopener,noreferrer');
  };

  const programs = [
    {
      icon: Home,
      title: 'Emergency Housing Assistance',
      description: 'Immediate support for families facing housing crises, including rent assistance and temporary shelter placement.',
    },
    {
      icon: Key,
      title: 'Homeownership Program',
      description: 'Comprehensive support for first-time homebuyers, from down payment assistance to closing cost grants.',
    },
    {
      icon: Hammer,
      title: 'Home Repair & Renovation',
      description: 'Critical repairs and accessibility modifications for low-income homeowners and elderly residents.',
    },
    {
      icon: Users,
      title: 'Transitional Housing',
      description: 'Supportive housing programs for individuals transitioning from homelessness to stable living situations.',
    },
  ];

  const initiatives = [
    {
      title: 'Veterans Housing Initiative',
      description: 'Dedicated housing support for veterans and their families, ensuring those who served have a safe place to call home.',
      impact: '200+ veterans housed',
    },
    {
      title: 'Family Stability Program',
      description: 'Preventing family homelessness through early intervention, financial assistance, and case management services.',
      impact: '500+ families stabilized',
    },
    {
      title: 'Senior Safe Home',
      description: 'Keeping seniors in their homes through modifications, repairs, and support services that promote independence.',
      impact: '150+ seniors assisted',
    },
  ];

  const eligibility = [
    'Income at or below 80% of Area Median Income',
    'U.S. citizen or eligible non-citizen',
    'Demonstrate housing need or crisis',
    'Willing to participate in program requirements',
    'Priority given to veterans, seniors, and families with children',
    'Must be a resident of our service area',
  ];

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="housing-page">
      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1E3A8A] mb-4">
              Our Programs
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#0B1120] tracking-tight leading-tight mb-6">
              Housing Project
            </h1>
            <p className="text-xl text-slate-600 font-light leading-relaxed mb-8">
              Everyone deserves a safe place to call home. Our housing initiatives provide 
              emergency assistance, homeownership support, and long-term solutions for 
              individuals and families in need throughout our community.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate('/')}
                className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-8 py-6 text-lg rounded-full"
                data-testid="housing-apply-btn"
              >
                Apply for Assistance
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-medium text-[#0B1120] mb-4">
              Our Housing Programs
            </h2>
            <p className="text-lg text-slate-600 font-light max-w-2xl mx-auto">
              Comprehensive support from crisis intervention to long-term housing stability.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {programs.map((program, idx) => (
              <motion.div
                key={program.title}
                className="p-8 rounded-2xl border border-slate-200 hover:border-[#1E3A8A]/30 hover:shadow-lg transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <div className="w-14 h-14 rounded-xl bg-[#DBEAFE] flex items-center justify-center mb-6">
                  <program.icon className="w-7 h-7 text-[#1E3A8A]" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-3 font-serif">
                  {program.title}
                </h3>
                <p className="text-slate-600 font-light">
                  {program.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Special Initiatives */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-medium text-[#0B1120] mb-4">
              Special Initiatives
            </h2>
            <p className="text-lg text-slate-600 font-light max-w-2xl mx-auto">
              Targeted programs addressing the unique needs of our most vulnerable community members.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {initiatives.map((initiative, idx) => (
              <motion.div
                key={initiative.title}
                className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E3A8A] text-sm font-medium rounded-full mb-4">
                  {initiative.impact}
                </span>
                <h3 className="text-xl font-semibold text-slate-800 mb-3 font-serif">
                  {initiative.title}
                </h3>
                <p className="text-slate-600 font-light">
                  {initiative.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-medium text-[#0B1120] mb-6">
                Eligibility Requirements
              </h2>
              <p className="text-lg text-slate-600 font-light mb-8">
                We strive to help as many people as possible while ensuring our resources 
                reach those who need them most.
              </p>
              <div className="space-y-4">
                {eligibility.map((item, idx) => (
                  <motion.div
                    key={idx}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <CheckCircle className="w-5 h-5 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80"
                alt="Family in new home"
                className="rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#1E3A8A] text-white p-6 rounded-xl shadow-lg">
                <p className="text-3xl font-bold font-serif">850+</p>
                <p className="text-sm text-white/80">Families Housed</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-[#0B1120] text-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Heart className="w-12 h-12 mx-auto mb-6 text-red-400" />
            <h2 className="text-3xl sm:text-4xl font-medium mb-6">
              Help Us Build Stable Communities
            </h2>
            <p className="text-lg text-slate-300 font-light mb-8">
              Your support helps provide emergency housing assistance, down payment grants, 
              and critical home repairs for families in need.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => { navigate('/'); setTimeout(() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
                className="bg-white text-[#1E3A8A] hover:bg-slate-100 px-10 py-6 text-lg rounded-full"
              >
                Partner With Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HousingPage;
